import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import Divider from '@mui/material/Divider/index';
import {
  clearFilter,
  setFilterData,
  setFilterStatus,
  updateFilterData,
  clearSourceFilterData,
  setFilterAppliedStatus
} from '#action/list-filter';
import {
  MultipleSelect,
  RadioButtonsGroup,
  MultipleCheckboxes,
  SelectWithCounter,
  SelectedFiltersChips,
  DateRangeButton,
  MobileFilterToggle
} from './components/general';
import {
  MultipleSelect as MobileMultipleSelect,
  MobileRadioButtons
} from './components/mobile';
import {
  SubprojectRadioButtonsGroup,
  MobileSubprojectRadioButtonsGroup
} from './components/project';
import { useApplyFilterToUrl } from './hooks/useUpdateURLParams';
import {
  useSyncFilter,
  useFilterPristine,
  useUpdateInitialState,
  useUpdateLocalStorage
} from './hooks';
import { Wrapper, FilterOptionsContainer } from './filter.styles';
import { updateURLParameter, getURLParameter } from '#helper';

const defaultComponents = {
  general: {
    MultipleSelect,
    RadioButtonsGroup,
    MultipleCheckboxes,
    SubprojectRadioButtonsGroup,
    SelectWithCounter
  },
  mobile: {
    MultipleSelect: MobileMultipleSelect,
    RadioButtonsGroup: MobileRadioButtons,
    MultipleCheckboxes,
    SubprojectRadioButtonsGroup: MobileSubprojectRadioButtonsGroup,
    SelectWithCounter
  }
};

const defaultActions = {
  clearFilter,
  setFilterData,
  setFilterStatus,
  updateFilterData,
  clearSourceFilterData
};

const FilterWrapper = ({ data = {} }) => {
  const { customActions, dateRangeComponent, disableSync } = data;
  const theme = useTheme();
  const dispatch = useDispatch();
  const [pendingFetch, setPendingFetch] = useState(false);
  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const handleUpdateInitState = useUpdateInitialState();
  const applyFilterToURL = useApplyFilterToUrl();
  const applyFilterToLS = useUpdateLocalStorage(disableSync);

  const actions = useMemo(() => ({
    ...defaultActions,
    ...customActions
  }), [customActions]);

  const resetPagination = useCallback(() => {
    if (actions.changePage) {
      dispatch(actions.changePage(1, { refetch: false }));
    }
    const currentPage = +getURLParameter('page');
    if (currentPage !== 1 && currentPage !== null) {
      updateURLParameter('page', 1);
    }
  }, [actions, dispatch]);

  const handleFetchFilterData = useCallback(() => {
    applyFilterToURL();
    applyFilterToLS();

    resetPagination();

    dispatch(actions.fetchFilterData());
    handleUpdateInitState();
    dispatch(setFilterAppliedStatus(true));
  }, [
    actions,
    dispatch, handleUpdateInitState,
    resetPagination, applyFilterToURL, applyFilterToLS
  ]);

  const handleChange = useCallback((value, name, id) => {
    dispatch(setFilterAppliedStatus(false));
    dispatch(actions.updateFilterData(value, name, id));
    setPendingFetch(true);
  }, [actions, dispatch]);

  useEffect(() => {
    if (pendingFetch) {
      setPendingFetch(false);
      handleFetchFilterData();
    }
  }, [pendingFetch, handleFetchFilterData]);

  useSyncFilter(actions, disableSync);
  useFilterPristine();

  const getComponents = useCallback(() => {
    const components = data.components || [];
    const rendered = components.map(componentData => {
      const { type, ...props } = componentData;
      const view = isMobile ? 'mobile' : 'general';
      const Component = defaultComponents[view][type];

      if (Component) {
        const { id, name, relatedTo } = props;
        return <Component
          key={id}
          onChange={value => handleChange(value, name, relatedTo)}
          {...props}
        />;
      }
      return null;
    });

    return rendered;
  }, [data.components, handleChange, isMobile]);

  const renderedComponents = useMemo(() => getComponents(), [getComponents]);

  const selectWithCounterFilters = useMemo(() => {
    const components = data.components || [];
    return components
      .filter(c => c.type === 'SelectWithCounter')
      .map(c => ({
        name: c.name,
        label: c.label,
        relatedTo: c.relatedTo
      }));
  }, [data.components]);

  const relatedTo = selectWithCounterFilters[0]?.relatedTo;

  const filterState = useSelector(s => s.listFilter?.[relatedTo], shallowEqual);

  const totalFilterCount = useMemo(() => {
    let count = 0;

    selectWithCounterFilters.forEach(filter => {
      const filterValue = filterState?.[filter.name]?.value;
      if (Array.isArray(filterValue)) {
        count += filterValue.length;
      }
    });

    if (dateRangeComponent && filterState?.start?.value && filterState?.end?.value) {
      count += 1;
    }

    return count;
  }, [selectWithCounterFilters, filterState, dateRangeComponent]);

  const handleChipRemove = useCallback(() => {
    setPendingFetch(true);
  }, []);

  const handleToggleFilters = useCallback(() => {
    setFiltersExpanded(prev => !prev);
  }, []);

  const showFiltersContent = !isMobile || filtersExpanded;

  return (
    <Wrapper>
      {isMobile && (
        <MobileFilterToggle
          isOpen={filtersExpanded}
          onToggle={handleToggleFilters}
          filterCount={totalFilterCount}
          label="Filters"
        />
      )}
      {showFiltersContent && (
        <>
          <FilterOptionsContainer>
            {renderedComponents}
            {dateRangeComponent && (
              <DateRangeButton
                relatedTo={relatedTo}
                onChange={handleChange}
                label="Select date range"
              />
            )}
          </FilterOptionsContainer>
          <div>
            <Divider style={{ paddingTop: 16, width: '100%' }} />
            {(selectWithCounterFilters.length > 0 || dateRangeComponent) && (
              <SelectedFiltersChips
                filters={selectWithCounterFilters}
                relatedTo={relatedTo}
                onRemove={handleChipRemove}
                showDateRange={dateRangeComponent}
              />
            )}
          </div>
        </>
      )}
    </Wrapper>
  );
};

export default FilterWrapper;
